<template>
  <motlet-neo :options="motletOptions">
    <template v-slot:content="{ props }">
      <!-- eslint-disable-next-line prettier/prettier -->
      <line-chart
        ref="lineChart"
        :settings="settings"
        :motlet="props.motlet"
        :store-name="storeName"
      />
    </template>
  </motlet-neo>
</template>

<script>
import { subMilliseconds } from 'date-fns';
import { merge, isFunction } from 'lodash';
import { format } from 'date-fns';
import { api } from '@/api/common/instances';
import { comma } from '@/utils/formatter';
import motletable from '@/mixins/motletable';
import chartable from '@/mixins/chartable';
import LineChart from './LineChart';

const LEVELS = { critical: '#ff0000', warning: '#ffdd00', info: '#0085ff', failback: '#00ff00' };

export default {
  components: {
    LineChart,
  },
  mixins: [motletable, chartable],
  props: {
    sstmId: {
      type: String,
      required: true,
    },
    storeName: {
      type: String,
      required: true,
    },
    datetime: {
      type: Date,
      required: true,
    },
    isFocus: {
      type: Boolean,
      required: true,
    },
    chartHeight: Number,
  },
  data() {
    return {
      motletOptions: {
        id: 'analysis/alert/owAlert',
        title: 'alert.chart.title',
        monitor: {
          systemId: this.sstmId,
          // BEGIN::Commented.by.KWShin.2024.08.21.
          // OWLens Alert은 원래 모니터코드가 없는 항목이어서 아무거나 할당해 놓은 것임.
          monitorCode: 'C_ABAP_DUMP',
          eventCode: 'ABAP_DUMP',
          valueField: 'count',
          // FINISH::Commented.by.KWShin.2024.08.21.
          points: 180,
        },
        zoom: {
          use: true,
          active: false,
        },
        settings: { chart: { use: false } },
        descendant: {
          systemId: this.sstmId,
        },
      },
      data: [],
    };
  },
  computed: {
    settings() {
      if (this.data.length === 0) {
        return {};
      }
      const xAxis = {
        data: this.data.map(({ dateTime }) => dateTime),
        axisLabel: {
          interval: Math.ceil(this.data.length / 6),
        },
      };
      const yAxis = {
        name: 'Count',
        max: undefined,
        maxInterval: undefined,
        splitNumber: undefined,
      };
      const series = this.getSeries();
      const formatter = {
        tooltip: params => {
          const contents = [];
          contents.push('<i class="v-icon notranslate mdi mdi-clock-time-four-outline mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(format(parseInt(params[0].axisValue), 'yyyy-MM-dd HH:mm:ss'));
          for (const param of params.reverse()) {
            contents.push('<br />');
            contents.push(this.circle(param.color));
            contents.push(`<span style="color:${param.color};font-weight:900;">`);
            contents.push(param.seriesId);
            contents.push('</span> : ');
            contents.push(comma(param.data));
          }
          return contents.join('');
        },
      };
      const defaults = {
        chart: {
          xAxis,
          yAxis,
          series,
        },
        formatter,
        systemId: this.sstmId,
        datetime: this.datetime,
      };
      return merge({}, defaults, isFunction(this.getSettings) ? this.getSettings() : undefined);
    },
  },
  watch: {
    sstmId: {
      handler(value) {
        this.setDescendants(value);
        this.fetchData();
      },
    },
    datetime: {
      handler() {
        this.fetchData();
      },
    },
    'monitor.cycle': {
      handler() {
        this.fetchData();
      },
    },
    /**
     * 이걸 watch해서 다시 그려주어야
     * 메뉴에서 ContextAnalyer 화면을 선택해 들어왔을 때
     * 차트가 그려진다. 즉, 그외의 값들을 메뉴를 통해 들어왔을 때 변화가 발생안한다는 거지...
     */
    isFocus: {
      immediate: true,
      handler(value) {
        if (value === true) this.fetchData();
      },
    },
    /**
     * 화면에서 차트 영역의 높이가 변했을 때 차트의 높이를 변경해준다.
     * 이 lineCharable.js 모듈을 사용하는 컴포넌트들이 expansion panel 내에 위치했을 때
     * panel을 오므렸다가 펼치면 차트가 화면에 재생되지 않는 현상이 존재한다.
     * 그래서, panel이 다시 펼쳐졌을 때 차트를 다시 그려주어야 하는데
     * 이 위치에서 차트의 options를 변경하기에 편리한 방법은 fetchData()를 다시 실행하여
     * options 내의 data를 변경하는 방법이어서 아래와 같이 구현하였다.
     * 차트 영역의 높이는 여러가지 상황에서 변화되기 때문에 panel을 오므렸다가 다시 펼치는
     * 상황에서만 동작하여 fetchData()의 실행 횟수를 최소화하기 위하여 여러가지 조건을 체크하였다.
     */
    chartHeight: {
      handler(newValue, oldValue) {
        if (newValue) {
          this.$refs.lineChart.$refs.chart.$refs.chart.$el.style.height = `${newValue}px`;
          if (this.data.length > 0 && newValue > 0 && oldValue <= 0) {
            /*
             * 야... 이건 진짜... 심하네...
             * 화면의 넓이가 변하면 숨어있던 expansion panel이나 tab 내의 차트의 넓이가 0으로 바뀌어버려서
             * 숨어있던 panel이나 tab이 다시 나타나도 차트가 표시되지 않는다.
             * 그래서, 차트 wrapper의 넓이로 차트의 넓이를 셋팅해주려 하는데 wrapper의 크기가 반영되는 시점 잡기가 만만치 않다.
             * 이전의 크기를 저장했다가 사용하니 크기가 넓이가 변했을 때 차트의 넓이가 이전 넓이로 그려지는 문제가 있고
             * $nextTick()으로도 안되고, setTimeout()을 하니 첫번째 차트가 가끔씩 안그려진다.
             * setTimeout(50)을 하니 별 무리 없다.
             */
            const vm = this;
            setTimeout(() => {
              const w = vm.$refs.lineChart.$refs.chart.$refs.chart.$el.parentNode.clientWidth;
              vm.$refs.lineChart.$refs.chart.$refs.chart.$el.style.width = `${w}px`;
            }, 10);

            this.fetchData();
          }
        }
      },
    },
  },
  methods: {
    getSeries() {
      const series = [];
      for (const level in LEVELS) {
        series.push({
          id: level,
          type: 'line',
          data: this.data.map(d => d[level]),
          itemStyle: {
            color: LEVELS[level],
          },
        });
      }
      return series;
    },
    /**
     * 총 3분 동안의 데이터를 조회한다. 초단위이므로 180개의 데이터가 서버에서 내려온다.
     * 이빨이 빠진 데이터도 서버에서 채워서 내려준다.
     * 2024.08.21.KWShin.
     */
    async fetchData() {
      const bounds = this.getBounds();
      if (!bounds) return;
      const params = {
        ...bounds,
        sstmId: this.sstmId,
      };
      ({ data: this.data } = await api.get('/alert-chart', { params }));
    },
    getBounds() {
      let endTimeMillis = Math.round(this.datetime.getTime() / 1000) * 1000;
      let quater = Math.round(this.motletOptions.monitor.points / 4);
      let endTime = new Date(endTimeMillis + quater * 1000);
      let beginDateTime = subMilliseconds(endTime, (this.motletOptions.monitor.points - 1) * 1000).getTime();
      let endDateTime = endTime.getTime();

      return {
        beginDateTime,
        endDateTime,
      };
    },
  },
};
</script>
